import { useEffect, useMemo, useRef, useState } from 'react';
import { CaretDown, MagnifyingGlass, Tag, X } from '@phosphor-icons/react';

type TagOption = {
  id: string;
  name: string;
};

type TagSelectorProps = {
  tags: TagOption[];
  value: TagOption[];
  t: any;
  onChange: (tags: TagOption[]) => void;
  tone?: string;
};

export default function TagSelector({ tags, value, t, onChange, tone = '#2d1b2e' }: TagSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selectedIds = useMemo(() => new Set(value.map((tag) => tag.id)), [value]);
  const options = tags.filter((tag) => (
    !selectedIds.has(tag.id)
    && tag.name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())
  ));

  useEffect(() => {
    const closeOnPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && !rootRef.current?.contains(target)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', closeOnPointerDown, true);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnPointerDown, true);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  const select = (tag: TagOption) => {
    onChange([...value, tag]);
    setSearch('');
    setOpen(false);
  };

  return (
    <div className="tag-selector" ref={rootRef} style={{ '--tag-tone': tone } as any}>
      <button
        type="button"
        className="tag-selector-trigger"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <Tag />
        <span>{value.length ? t.selectedTags.replace('{count}', value.length) : t.selectTags}</span>
        <CaretDown />
      </button>
      {value.length > 0 && (
        <div className="selected-tags">
          {value.map((tag) => (
            <span key={tag.id}>
              {tag.name}
              <button
                type="button"
                aria-label={`${t.removeTag} ${tag.name}`}
                onClick={() => onChange(value.filter((item) => item.id !== tag.id))}
              >
                <X />
              </button>
            </span>
          ))}
        </div>
      )}
      {open && (
        <div className="tag-selector-dropdown">
          <label>
            <MagnifyingGlass />
            <input
              autoFocus
              value={search}
              onChange={(event: any) => setSearch(event.target.value)}
              placeholder={t.searchTags}
            />
          </label>
          <div className="tag-selector-options">
            {options.map((tag) => (
              <button type="button" key={tag.id} onClick={() => select(tag)}>
                <Tag />{tag.name}
              </button>
            ))}
            {!options.length && <p>{tags.length ? t.noMatchingTags : t.noTagsYet}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
